import React from "react";

import {
  AccountCircleRounded,
  BorderColorRounded,
  ForwardToInboxRounded,
  LogoutRounded,
  MenuRounded,
  WarningRounded,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import useAuth from "../../config/context/authContext";
import useMainState from "../../config/context/mainStateContext";
import useModal from "../../config/context/modalContext";
import { SpottService } from "../../config/services/SpottService";
import { GreyPallet } from "../../config/styles";

import MainBorderButton from "../../components/MainBorderButton";
import MainSolidButton from "../../components/MainSolidButton";
import MainText from "../../components/MainText";
import ModalUserName from "../../components/ModalUserName";
import PageHeader from "../../components/PageHeader";
import LoginForm from "./LoginForm";
import SigninForm from "./SigninForm";
import ResetPasswordForm from "./ResetPasswordForm";
import { Dev } from "../../config/util/consoleUtil";

export enum AvailableFormOptions {
  signin = 0,
  login = 1,
  reset = 2,
}

export default function AccountPage() {
  const { currentUserState, currentUserInfo, authLogOutUser, authConfirmEmail } = useAuth();
  const navigate = useNavigate();

  const { pushAlert } = useModal();
  const { setShowSideMenuDrawer } = useMainState();

  const [showModalUserName, setShowModalUserName] = React.useState(false);
  const [sendingEmail, setSendingEmail] = React.useState(false);

  // const [colorNames, setColorNames] = React.useState(colorList);

  const [formOption, setFormOption] = React.useState<AvailableFormOptions>(
    AvailableFormOptions.login
  );

  const handleSendEmail = async () => {
    try {
      setSendingEmail(true);
      await authConfirmEmail();
      pushAlert({
        type: "success",
        message: "Email enviado",
      });
    } catch (error) {
      console.log(...new Dev("handleSendEmail", error).Error());
      pushAlert({
        type: "warning",
        message: "Erro ao enviar email",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  React.useEffect(() => {}, []);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          title={
            currentUserState
              ? "Perfil"
              : formOption === 1
              ? "Login"
              : formOption === 0
              ? "Criar Conta"
              : "Resetar Senha"
          }
          leftIcon={<MenuRounded />}
          onLeftAction={() => setShowSideMenuDrawer(true)}
          rightIcon={currentUserState ? <LogoutRounded /> : undefined}
          onRightAction={() => {
            if (currentUserState) authLogOutUser();
          }}
        />

        {showModalUserName && <ModalUserName onClose={() => setShowModalUserName(false)} />}

        {currentUserState ? (
          <Box>
            {!currentUserState.emailVerified ? (
              <Box
                mt={2}
                sx={{ p: 2 }}
                style={{ backgroundColor: GreyPallet.Light, borderRadius: 15 }}
              >
                <MainText color="regular">
                  Por segurança, para utilizar todas as funcionalidades do aplicativo é necessário
                  confirmar o seu email.
                </MainText>

                <Box mt={3}>
                  <MainText color="regular">
                    Clique abaixo e te enviaremos um link para confirmar sua identidade. Lembre de
                    checar sua caixa de spam.
                  </MainText>
                </Box>

                <Box mt={2}>
                  <MainSolidButton
                    loading={sendingEmail}
                    text="Enviar Email"
                    icon={<ForwardToInboxRounded />}
                    onClick={handleSendEmail}
                  />
                </Box>
              </Box>
            ) : null}

            <Box mt={2} display={"flex"} justifyContent={"start"} alignItems={"center"}>
              <AccountCircleRounded
                sx={{
                  mr: 1,
                  fontSize: "36px",
                  color: currentUserInfo?.color || GreyPallet.Black,
                }}
              />

              <MainText color={currentUserInfo?.name ? "black" : "regular"} size="subtitle">
                {currentUserInfo?.name || "Sem Nome"}
              </MainText>

              {!currentUserInfo?.name && <WarningRounded color="disabled" sx={{ ml: 1 }} />}
            </Box>

            <Box mt={2}>
              <MainText color="black" size="subtitle">
                {currentUserState?.email || ""}
              </MainText>
            </Box>

            <Box mt={2}>
              <MainText color="regular" size="body">
                {DateTime.fromRFC2822(currentUserState?.metadata?.creationTime || "").toFormat(
                  "dd/LL/y HH:mm"
                )}
              </MainText>
            </Box>

            <Box mt={2}>
              <MainText color="regular" size="body">
                {currentUserState.emailVerified && currentUserInfo?.enabled
                  ? "Usuário habilitado"
                  : "Usuário não habilitado"}
              </MainText>
            </Box>

            <Box
              mt={3}
              sx={{ p: 2 }}
              style={{ backgroundColor: GreyPallet.Light, borderRadius: 15 }}
            >
              <MainText color="regular">
                Não se preocupe. Apenas o seu nome ficará visível para outros usuários
              </MainText>
            </Box>

            <Box mt={2}>
              <MainBorderButton
                text="Editar Nome"
                icon={<BorderColorRounded />}
                onClick={() => setShowModalUserName(true)}
              />
            </Box>
          </Box>
        ) : formOption === 0 ? (
          <SigninForm setFormOption={setFormOption} />
        ) : formOption === 1 ? (
          <LoginForm setFormOption={setFormOption} />
        ) : (
          <ResetPasswordForm setFormOption={setFormOption} />
        )}
      </Box>
    </Box>
  );
}
